/* @sravni/react-design-system version 0.2.60 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStyles, useFormControlContext, useBoolean, useId, isDefined, safeInvoke, useMergedRef } from '@sravni/react-utils';
import { fixControlledValue } from '../../TextInput/src/TextInput.js';
import '../../Spinner/index.js';
import '../../Icon/index.js';
import modules_dd9d698d from '../styles/Textarea.module.less.js';
import Spinner from '../../Spinner/src/Spinner.js';
import Icon from '../../Icon/src/Icon.js';

var MIN_ROWS = 1;
var MAX_ROWS = 10;
/**
 * @description The Text Area component allow user to type in longer content
 *
 * @component
 * @example
 * ```jsx
 * <Textarea value="content" />
 * ```
 */
var Textarea = React.forwardRef(function (props, ref) {
    var _a;
    var id = props.id, value = props.value, label = props.label, placeholder = props.placeholder, resizable = props.resizable, icon = props.icon, disabled = props.disabled, invalid = props.invalid, readOnly = props.readOnly, loading = props.loading, rows = props.rows, positionInGroup = props.positionInGroup, directionGroup = props.directionGroup, background = props.background, onFocus = props.onFocus, onBlur = props.onBlur, onChange = props.onChange, onClick = props.onClick, style = props.style, className = props.className, restProps = __rest(props, ["id", "value", "label", "placeholder", "resizable", "icon", "disabled", "invalid", "readOnly", "loading", "rows", "positionInGroup", "directionGroup", "background", "onFocus", "onBlur", "onChange", "onClick", "style", "className"]);
    var cx = useStyles(modules_dd9d698d);
    var formControlContext = useFormControlContext();
    var inputRef = React.useRef();
    var containerRef = React.useRef(null);
    var _b = __read(useBoolean(false), 2), focused = _b[0], setFocused = _b[1];
    var _c = __read(useBoolean(value !== undefined && value !== ''), 2), filled = _c[0], setFilled = _c[1];
    var _d = __read(React.useState(rows || 1), 2), innerRows = _d[0], setInnerRows = _d[1];
    var inputId = id !== null && id !== void 0 ? id : useId('textarea');
    var hasIcon = icon || loading;
    var isFocused = focused && !readOnly;
    var isDisabled = formControlContext.disabled || disabled;
    var isInvalid = formControlContext.invalid || invalid;
    var classNames = cx(className, 'container', (_a = {
            focused: focused,
            disabled: formControlContext.disabled || disabled,
            invalid: formControlContext.invalid || invalid,
            'with-label': isDefined(label)
        },
        _a["background-" + background] = background,
        _a["group-" + directionGroup] = directionGroup,
        _a["group-" + positionInGroup] = positionInGroup,
        _a));
    var classNamesLabel = cx('label', {
        focused: isFocused,
        filled: filled,
        disabled: isDisabled,
        invalid: isInvalid,
        'with-icon': hasIcon,
    });
    var classNamesPlaceholder = cx('placeholder', {
        focused: isFocused,
        filled: filled,
        disabled: isDisabled,
        invalid: isInvalid,
        'with-icon': hasIcon,
        withLabel: label && focused,
        hidden: label && !focused,
    });
    var classNamesTextarea = cx('textarea', {
        focused: focused && !readOnly,
        filled: filled,
        disabled: isDisabled,
        invalid: isInvalid,
        'with-icon': hasIcon,
        resize: resizable,
        scroll: innerRows === MAX_ROWS,
    });
    var iconElement = React.useMemo(function () {
        if (loading) {
            return React.createElement(Spinner, { className: cx('icon') });
        }
        if (icon) {
            return React.createElement(Icon, { className: cx('icon'), icon: icon });
        }
        return null;
    }, [icon, loading]);
    var labelElement = React.useMemo(function () {
        if (label) {
            return (React.createElement("label", { htmlFor: inputId, className: classNamesLabel }, label));
        }
        return null;
    }, [inputId, label, filled, focused, classNamesLabel]);
    var placeholderElement = React.useMemo(function () {
        if ((label && readOnly) || filled) {
            return null;
        }
        if (placeholder) {
            return (React.createElement("label", { htmlFor: inputId, className: classNamesPlaceholder }, placeholder));
        }
        return null;
    }, [inputId, label, placeholder, filled, readOnly, classNamesPlaceholder]);
    var setRows = React.useCallback(function () {
        if (!inputRef.current) {
            return;
        }
        var textareaLineHeight = 20;
        var previousRows = inputRef.current.rows;
        inputRef.current.rows = rows !== null && rows !== void 0 ? rows : MIN_ROWS;
        var currentRows = ~~(inputRef.current.scrollHeight / textareaLineHeight);
        if (currentRows === previousRows) {
            inputRef.current.rows = currentRows;
        }
        if (currentRows >= MAX_ROWS) {
            inputRef.current.rows = MAX_ROWS;
            inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }
        currentRows && setInnerRows(currentRows < MAX_ROWS ? currentRows : MAX_ROWS);
    }, [inputRef, rows]);
    var handleClick = React.useCallback(function (event) {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        setFocused.on();
        safeInvoke(onClick, event);
    }, [onClick]);
    var handleFocus = React.useCallback(function (event) {
        if (!focused) {
            setFocused.on();
            safeInvoke(onFocus, event);
        }
    }, [focused, onFocus]);
    var handleBlur = React.useCallback(function (event) {
        if (event.relatedTarget === containerRef.current)
            return;
        setFocused.off();
        safeInvoke(onBlur, event);
    }, [onBlur]);
    var handleChange = React.useCallback(function (event) {
        resizable && setRows();
        safeInvoke(onChange, event);
    }, [resizable, setRows, onChange]);
    var handleContainerBlur = function (event) {
        var _a;
        //needs to prevent extra onBlur callback executing on Textarea wrapper
        var relTarget = event.relatedTarget;
        if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(relTarget)) {
            event.stopPropagation();
        }
    };
    var handleContainerFocus = function (event) {
        //needs to prevent extra onFocus callback executing on Textarea wrapper
        var relTarget = event.relatedTarget;
        if (relTarget === containerRef.current) {
            event.stopPropagation();
        }
    };
    React.useEffect(function () {
        if (value !== undefined && value !== '') {
            setFilled.on();
        }
        else {
            setFilled.off();
        }
    }, [value]);
    React.useEffect(function () {
        resizable && setRows();
    }, [resizable, value, setRows]);
    return (React.createElement("div", { ref: containerRef, "data-qa": "Textarea", className: classNames, style: style, onClick: handleClick, onBlur: handleContainerBlur, onFocus: handleContainerFocus, tabIndex: -1 },
        React.createElement("textarea", __assign({ id: inputId, spellCheck: "false", rows: innerRows, ref: useMergedRef(inputRef, ref), value: fixControlledValue(value), disabled: formControlContext.disabled || disabled, readOnly: readOnly, className: classNamesTextarea, onBlur: handleBlur, onFocus: handleFocus, onChange: handleChange }, restProps)),
        labelElement,
        placeholderElement,
        iconElement));
});
Textarea.defaultProps = {
    rows: 1,
    resizable: true,
};
Textarea.displayName = 'Textarea';

export default Textarea;
//# sourceMappingURL=Textarea.js.map
